// Public R2 URLs for static site SVGs (avatar placeholder, 404 illustration).
// The base host is read from the build-time `VITE_R2_PUBLIC_URL` config
// (mirrors the server's `R2_PUBLIC_URL`) so staging/dev/prod and white-label
// deployments can each point at their own bucket without a code change.
// The fallback is the current production R2 public CDN host — same one used
// by `PAYMENT_ICON_BASE` in CheckoutPage — so the URLs keep working when
// the env var is not configured.
import { publicImageUrl, PUBLIC_ASSET_ORIGIN } from "../../../../lib/copy/src/public-image-url.mjs";
const FALLBACK_R2_PUBLIC_URL = PUBLIC_ASSET_ORIGIN;

const env = (typeof import.meta !== "undefined"
  ? (import.meta as { env?: { VITE_R2_PUBLIC_URL?: string } }).env
  : undefined) || {};

const R2_PUBLIC_URL = publicImageUrl(env.VITE_R2_PUBLIC_URL || FALLBACK_R2_PUBLIC_URL).replace(/\/$/, "");

function siteAssetUrl(key: string): string {
  return `${R2_PUBLIC_URL}/site-assets/${key}`;
}

export const USER_PLACEHOLDER_AVATAR_URL = siteAssetUrl("user-no-photo-template.svg");
export const NOT_FOUND_ILLUSTRATION_URL = siteAssetUrl("error.svg");

// Default Open Graph / Twitter share card (the homepage/generic-page social
// preview). A clean, designed 1200×630 branded card — classic-car hero photo,
// white wordmark, tagline, no UI chrome — hosted in R2 under site-assets/ so it
// is guaranteed live at the URL the meta tag references. Generated + uploaded by
// `artifacts/api-server/scripts/upload-og-share-card.ts`. The legacy
// www.bookaclassic.com host does NOT serve this frontend's build, so the meta
// tags point at the R2 CDN URL rather than a same-origin /opengraph.jpg path.
export const DEFAULT_OG_IMAGE_URL = siteAssetUrl("opengraph.jpg");
export const DEFAULT_OG_IMAGE_WIDTH = 1200;
export const DEFAULT_OG_IMAGE_HEIGHT = 630;

// Marketing photos for the Pro Owner page, hosted in R2 under site-assets/ with
// a 1-year immutable cache (uploaded by
// `artifacts/api-server/scripts/upload-proowner-marketing.ts`). Each has an
// optimized WebP sibling for `<picture>` plus the original JPG as a fallback.
// They previously streamed through the slow Replit Object Storage proxy.
export interface MarketingImage {
  webp: string;
  fallback: string;
}

export function marketingImage(jpgFileName: string): MarketingImage {
  const base = jpgFileName.replace(/\.[^.]+$/, "");
  return {
    webp: siteAssetUrl(`${base}.webp`),
    fallback: siteAssetUrl(jpgFileName),
  };
}

// Official APEX brand mark hosted in R2 under site-assets/apex/.
// Uploaded by `artifacts/api-server/scripts/upload-apex-logo.ts`.
export const APEX_LOGO_SIZES = [16, 32, 64, 128, 256] as const;
export type ApexLogoSize = (typeof APEX_LOGO_SIZES)[number];
export const APEX_LOGO_VARIANTS: Record<ApexLogoSize, string> = {
  16: siteAssetUrl("apex/apex-peak-16.png"),
  32: siteAssetUrl("apex/apex-peak-32.png"),
  64: siteAssetUrl("apex/apex-peak-64.png"),
  128: siteAssetUrl("apex/apex-peak-128.png"),
  256: siteAssetUrl("apex/apex-peak-256.png"),
};

// Car-detail APEX-only recolour of the original silhouette, hosted with the web bundle.
export const APEX_GREEN_LOGO_VARIANTS: Record<ApexLogoSize, string> = {
  16: `${import.meta.env.BASE_URL}images/apex/apex-peak-green-16.png`,
  32: `${import.meta.env.BASE_URL}images/apex/apex-peak-green-32.png`,
  64: `${import.meta.env.BASE_URL}images/apex/apex-peak-green-64.png`,
  128: `${import.meta.env.BASE_URL}images/apex/apex-peak-green-128.png`,
  256: `${import.meta.env.BASE_URL}images/apex/apex-peak-green-256.png`,
};
export const APEX_GREEN_DARK_LOGO_VARIANTS: Record<ApexLogoSize, string> = {
  16: `${import.meta.env.BASE_URL}images/apex/apex-peak-green-dark-16.png`,
  32: `${import.meta.env.BASE_URL}images/apex/apex-peak-green-dark-32.png`,
  64: `${import.meta.env.BASE_URL}images/apex/apex-peak-green-dark-64.png`,
  128: `${import.meta.env.BASE_URL}images/apex/apex-peak-green-dark-128.png`,
  256: `${import.meta.env.BASE_URL}images/apex/apex-peak-green-dark-256.png`,
};
